import Tippy from "@tippyjs/react";
import { useState } from "react";

import CompactionContextWindowInfoModal from "./CompactionContextWindowInfoModal";
import styles from "./CompactionGauge.module.scss";

interface ContextUsedGaugeProps {
  usedTokens: number;
  compactionTriggerTokens: number;
  truncatedMessages: number;
  compactionFailed: boolean;
}

const SIZE = 20;
const STROKE = 2.5;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default function CompactionGauge({
  usedTokens,
  compactionTriggerTokens,
  truncatedMessages,
  compactionFailed,
}: ContextUsedGaugeProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!compactionTriggerTokens || !usedTokens) return null;

  // How full the context is relative to the compaction trigger threshold.
  const usagePercent = (usedTokens / compactionTriggerTokens) * 100;
  const hasTruncation = truncatedMessages > 0;

  // Show the gauge when: usage is at least 50%, truncation happened, or
  // compaction failed/skipped.
  const shouldShowGauge =
    usagePercent >= 50 || hasTruncation || compactionFailed;
  if (!shouldShowGauge) return null;

  const clampedPercent = hasTruncation ? 100 : Math.min(usagePercent, 100);
  const ariaPercent = hasTruncation ? 100 : Math.round(usagePercent);

  // Priority order matters: compaction failure + truncation is the worst state
  // (summarization broke and messages were dropped), then compaction failure
  // alone, then usage past the compaction threshold, then normal (50-99%)
  // with a warning color at 80%+.
  let colorClass: string;
  let showExclamation: boolean;
  let tooltipContent: string;
  // Compaction failed and truncation happened: worst case, show error state and exclamation mark.
  if (compactionFailed && hasTruncation) {
    colorClass = styles.error;
    showExclamation = true;
    tooltipContent = `Summarization failed. ${truncatedMessages} earlier message${
      truncatedMessages === 1 ? "" : "s"
    } dropped to fit context window. Click to learn more.`;
    // Compaction failed but no truncation: show warning state and exclamation mark.
  } else if (compactionFailed) {
    colorClass = styles.compactionWarning;
    showExclamation = true;
    tooltipContent =
      "Summarization failed. You will still get a response, but summarization will be attempted again on the next message.";
    // The LLM response pushed usage past the threshold, but compaction only
    // triggers on the next message send — so between turns usage can exceed 100%.
  } else if (usagePercent >= 100) {
    colorClass = styles.compactionWarning;
    showExclamation = false;
    tooltipContent =
      "Auto-summarization will run on the next message. Click to learn more.";
    // Warn before compaction triggers so users aren't surprised by summarization.
  } else {
    // Usage is between 80% and 100% of the compaction threshold, show warning state.
    colorClass =
      usagePercent >= 80 ? styles.compactionWarning : styles.compaction;
    showExclamation = false;
    const remaining = Math.max(0, 100 - Math.round(usagePercent));
    tooltipContent = `${remaining}% context remaining until auto-summarization. Click to learn more.`;
  }

  const dashOffset = CIRCUMFERENCE - (clampedPercent / 100) * CIRCUMFERENCE;

  return (
    <>
      <Tippy content={tooltipContent} placement="top">
        <button
          type="button"
          className={`${styles.gauge} ${colorClass}`}
          aria-label="About context window usage"
          onClick={() => setIsModalOpen(true)}
        >
          <span
            role="img"
            aria-label={`Context used: ${ariaPercent}%`}
            className={styles.gaugeInner}
          >
            <svg
              width={SIZE}
              height={SIZE}
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className={styles.svg}
            >
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="var(--outline)"
                strokeWidth={STROKE}
              />
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth={STROKE}
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
                transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
              />
              {showExclamation && (
                <text
                  x={SIZE / 2}
                  y={SIZE / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="currentColor"
                  className={styles.overflowMark}
                >
                  !
                </text>
              )}
            </svg>
          </span>
        </button>
      </Tippy>
      {isModalOpen && (
        <CompactionContextWindowInfoModal
          onClose={() => setIsModalOpen(false)}
          compactionFailed={compactionFailed}
          truncatedMessages={truncatedMessages}
        />
      )}
    </>
  );
}
